import type { TInvestorReadinessAssessment } from "./types";

/**
 * Document type keyword mappings for score calculation
 */
const DOCUMENT_KEYWORDS: Record<string, string[]> = {
  "Pitch Deck": ["pitch", "deck", "presentation", "investor", "pitch deck"],
  "Business Plan": ["business", "plan", "model", "strategy", "business plan"],
  "Market Research": ["market", "research", "validation", "customer", "market research"],
  Financial: ["financial", "revenue", "projections", "statements", "financial projections", "financial statements"],
  Competitive: ["competitive", "competition", "differentiation", "usp", "unique selling", "competitive analysis"],
  Team: ["team", "biographies", "resumes", "org", "team biographies", "team composition"],
  "Legal/Compliance": ["legal", "compliance", "structure", "org chart", "legal compliance", "corporate structure"],
};

/**
 * Default keywords for general document types
 */
const DEFAULT_KEYWORDS = ["document", "data", "information", "material"];

/**
 * Get keywords for a document type
 */
export function getDocumentKeywords(documentName: string): string[] {
  const lowerName = documentName.toLowerCase();

  // Check for exact matches first
  for (const [docType, keywords] of Object.entries(DOCUMENT_KEYWORDS)) {
    if (lowerName.includes(docType.toLowerCase())) {
      return keywords;
    }
  }

  // Check for partial matches
  for (const [docType, keywords] of Object.entries(DOCUMENT_KEYWORDS)) {
    const docTypeLower = docType.toLowerCase();
    if (
      lowerName.includes(docTypeLower.split(" ")[0]) ||
      docTypeLower.split(" ").some((word) => lowerName.includes(word))
    ) {
      return keywords;
    }
  }

  return DEFAULT_KEYWORDS;
}

/**
 * Check if a text contains any of the keywords
 */
function containsKeywords(text: string, keywords: string[]): boolean {
  const lowerText = text.toLowerCase();
  return keywords.some((keyword) => lowerText.includes(keyword.toLowerCase()));
}

/**
 * Calculate a readiness score for a specific document type based on gaps and recommendations
 *
 * @param documentName - The name of the document/data type
 * @param assessment - The investor readiness assessment data
 * @returns A score between 0-100
 */
export function calculateDocumentScore(documentName: string, assessment: TInvestorReadinessAssessment): number {
  const keywords = getDocumentKeywords(documentName);

  // Count relevant gaps (each gap decreases score by 10)
  const relevantGaps = (assessment.gaps_or_weaknesses ?? []).filter((gap) => containsKeywords(gap, keywords));
  const gapCount = relevantGaps.length;

  // Count relevant recommendations (each recommendation decreases score by 5)
  const relevantRecommendations = (assessment.recommendations ?? []).filter((rec) => containsKeywords(rec, keywords));
  const recommendationCount = relevantRecommendations.length;

  // Base score starts at 100
  // Subtract points for gaps and recommendations
  let score = 100 - gapCount * 10 - recommendationCount * 5;

  // If there are no relevant gaps or recommendations, give a higher score
  // but still consider the overall readiness level
  if (gapCount === 0 && recommendationCount === 0) {
    // If overall readiness is low, still penalize slightly
    const baseReadinessLevel = assessment.readiness_level ?? 1;
    score = Math.max(70, 100 - (5 - baseReadinessLevel) * 5);
  }

  // Clamp score between 0 and 100
  return Math.max(0, Math.min(100, Math.round(score)));
}
